import React from 'react';
import { useParams } from 'react-router-dom';
import StandardAppLayout from 'src/shared/components/layout/standard-app-layout/StandardAppLayout';
import useFeatureFlag from 'src/shared/hooks/useFeatureFlag';
import { FeatureFlagId } from 'src/shared/types/cms/feature';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import Loader from 'src/shared/components/loader/Loader';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import Alert from 'src/shared/components/ui/alert';
import { useGetSiteNatureRiskOverviewQuery } from 'src/app/site-nature-risk/state/api/siteNatureRiskEngineSlice';
import ThreatenedSpeciesWidget from '../../components/threatened-species/ThreatenedSpeciesWidget';
import IucnRedListSummaryWidget from '../../components/threatened-species/IucnRedListSummaryWidget';

const SiteNatureRiskCharacteristics = () => {
    const { siteId } = useParams<{ siteId: string }>();
    const { currentCustomerId } = useCustomer();
    const navigation = useAppNavigation();
    // Characteristics is a sub-flag under Risk Insights — both must be enabled.
    const { enabled: riskInsightsEnabled, loading: riskInsightsLoading } = useFeatureFlag({ featureId: FeatureFlagId.RISK_INSIGHTS });
    const { enabled: characteristicsEnabled, loading: characteristicsLoading } = useFeatureFlag({
        featureId: FeatureFlagId.NATURE_RISK_SITE_CHARACTERISTICS,
    });
    // CMS truth for the dedicated flag, ignoring the internal-user override — lets us warn
    // internal users when they can see a page that is not yet live for the customer.
    const { enabled: characteristicsLiveInCms, loading: cmsCheckLoading } = useFeatureFlag({
        featureId: FeatureFlagId.NATURE_RISK_SITE_CHARACTERISTICS,
        ignoreInternalOverride: true,
    });
    const loading = riskInsightsLoading || characteristicsLoading;
    const enabled = riskInsightsEnabled && characteristicsEnabled;
    const showInternalOnlyBanner = !cmsCheckLoading && !characteristicsLiveInCms;

    // The threatened-species widget is self-fetching; this query only supplies
    // the site name for the header (cached — the overview view uses it too).
    const { data } = useGetSiteNatureRiskOverviewQuery(
        { organisationBusinessCentralId: currentCustomerId, siteId: siteId! },
        { skip: !currentCustomerId || !siteId }
    );

    if (loading) return <Loader />;
    if (!enabled) return <PageNotFound />;

    const site = data?.site;
    const breadcrumbs = [{ label: 'Nature Risk', path: navigation.globalNatureRiskOverviewPath }, { label: site?.name ?? '' }];

    return (
        <StandardAppLayout
            title={site?.name ?? ''}
            subTitle='Characteristics'
            breadcrumbs={breadcrumbs}
            stickyHeader={false}
            topBanner={
                showInternalOnlyBanner && (
                    <Alert severity='warning'>This page is only visible to internal users. It is not yet enabled for this customer.</Alert>
                )
            }
            mainContent={
                <div
                    className='grid w-full grid-cols-1 items-start gap-6 pt-1 md:grid-cols-2'
                    data-testid='site-nature-risk-characteristics-main'
                >
                    <div className='flex flex-col gap-6'>
                        <IucnRedListSummaryWidget organisationBusinessCentralId={currentCustomerId} siteId={siteId!} />
                        <ThreatenedSpeciesWidget organisationBusinessCentralId={currentCustomerId} siteId={siteId!} />
                    </div>
                </div>
            }
        />
    );
};

export default SiteNatureRiskCharacteristics;
